// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER SERVICE SJT — Question Bank
// 20 scenarios · Most/Least effective response format
// Science: McDaniel et al. (2001) SJT validity · Service orientation construct
// Market: BPO · Banking front-end · Retail · Hospitality · Customer support
// ─────────────────────────────────────────────────────────────────────────────
// Format: Each item has a scenario and 4 options.
// Candidate selects MOST effective AND LEAST effective response.
// Scoring: Most correct = 2pts, Least correct = 1pt, Either wrong = 0pt

export const SJT_CUSTOMER_ITEMS = [
  {
    id:"cs01", d:1,
    context:"You are a customer service representative at a bank. A customer approaches your counter very agitated and says loudly: 'I've been waiting for 40 minutes! This is completely unacceptable! I want to speak to your manager right now!'",
    opts:[
      "A: Apologise sincerely for the wait, acknowledge the customer's frustration and ask how you can help resolve the issue today",
      "B: Tell the customer that the wait time was not your fault and that you were busy with other customers",
      "C: Immediately call your manager without saying anything to the customer",
      "D: Ask the customer to calm down before you can assist them",
    ],
    most:0, least:1,
    exp_most:"Acknowledging frustration and focusing on resolution is the most effective customer service response — it de-escalates and moves forward.",
    exp_least:"Deflecting blame onto others is the least effective — it escalates frustration and damages trust."
  },
  {
    id:"cs02", d:1,
    context:"A customer calls your BPO helpline. After listening to their issue for two minutes, you realise the problem falls under a different department's remit and you will need to transfer them. The customer has already been transferred twice today.",
    opts:[
      "A: Transfer the customer immediately to the correct department",
      "B: Apologise for the repeated transfers, briefly explain why one more transfer is needed, give the customer the direct number in case they get disconnected, and transfer them",
      "C: Try to solve the issue yourself even though you don't have the authority or knowledge",
      "D: Ask the customer to call back another time when they are less frustrated",
    ],
    most:1, least:3,
    exp_most:"Empathy plus information (direct number) plus action is the ideal balance when a transfer is unavoidable.",
    exp_least:"Asking an already-frustrated customer to call back is dismissive and shows no customer focus."
  },
  {
    id:"cs03", d:2,
    context:"You work in a retail store. A customer wants to return a product that they purchased 35 days ago. Your store policy allows returns within 30 days. The product appears unused and the customer has the receipt.",
    opts:[
      "A: Strictly follow the policy and decline the return",
      "B: Immediately offer a full refund without checking with your supervisor",
      "C: Empathise with the customer, explain the policy, and offer to check with your supervisor if an exception can be made given the product's condition",
      "D: Tell the customer the policy is the policy and there is nothing you can do",
    ],
    most:2, least:3,
    exp_most:"Empathy, transparency and escalating to find a solution demonstrates customer focus while respecting process.",
    exp_least:"Dismissing the customer without any attempt at resolution destroys goodwill unnecessarily."
  },
  {
    id:"cs04", d:1,
    context:"You are handling a chat support session. The customer has sent three long messages describing a complex technical problem. You understand the issue but the solution requires about 10 minutes of work on your end.",
    opts:[
      "A: Begin working on the solution silently and respond only when you are done",
      "B: Acknowledge that you have understood the issue, let them know it will take approximately 10 minutes, and ask if they are happy to wait",
      "C: Ask the customer to summarise their problem more briefly",
      "D: Close the chat and raise a ticket for the technical team to handle",
    ],
    most:1, least:2,
    exp_most:"Setting expectations and seeking consent to proceed shows professionalism and respects the customer's time.",
    exp_least:"Asking a customer to re-explain after they have already given full details is frustrating and inefficient."
  },
  {
    id:"cs05", d:2,
    context:"A customer complains that they were charged twice for the same transaction. You check the system and can see only one charge. The customer is insisting they have a bank statement showing two charges.",
    opts:[
      "A: Tell the customer the system shows only one charge and they must be mistaken",
      "B: Apologise for the confusion, ask to see the bank statement if possible, raise a dispute investigation on their behalf and give them a reference number",
      "C: Immediately process a refund to avoid conflict",
      "D: Tell the customer to contact their bank directly as it is their bank's issue",
    ],
    most:1, least:0,
    exp_most:"Investigating properly and giving a reference number shows accountability and builds trust without making unauthorised refunds.",
    exp_least:"Dismissing the customer as mistaken without checking the evidence is disrespectful and may be factually wrong."
  },
  {
    id:"cs06", d:2,
    context:"You are working in a hotel. A guest complains at 11pm that the room next door has been very noisy for the past two hours and they have an early flight. They have already called the front desk once and nothing was done.",
    opts:[
      "A: Apologise and say you will note the complaint",
      "B: Apologise sincerely for the repeat inconvenience, call the noisy room directly to address it, and offer the guest a complimentary breakfast or room upgrade as a gesture",
      "C: Tell the guest that you cannot control other guests' behaviour",
      "D: Ask the guest to move rooms at 11pm",
    ],
    most:1, least:2,
    exp_most:"Taking action plus a goodwill gesture shows accountability for a previous failure to act.",
    exp_least:"Disclaiming responsibility for other guests' behaviour is a failure of service ownership."
  },
  {
    id:"cs07", d:1,
    context:"You are in the middle of explaining a product to a customer when another customer approaches and interrupts, demanding immediate attention.",
    opts:[
      "A: Stop helping the first customer and attend to the interrupting customer",
      "B: Acknowledge the second customer politely, let them know you will be with them shortly and continue helping the first customer",
      "C: Ignore the second customer and focus entirely on the first",
      "D: Call a colleague to take over from the first customer so you can help the second",
    ],
    most:1, least:0,
    exp_most:"Acknowledging the second customer without abandoning the first is the professional balance.",
    exp_least:"Abandoning a customer mid-conversation is disrespectful and unprofessional."
  },
  {
    id:"cs08", d:3,
    context:"A long-term customer has complained multiple times about the same issue over the past month. Each time, the issue was 'resolved' but then recurred. The customer is now threatening to leave.",
    opts:[
      "A: Apologise again and offer the same solution that has been applied before",
      "B: Escalate to a senior team member or manager, take personal ownership of ensuring a permanent fix is found, and proactively follow up with the customer in 48 hours",
      "C: Offer the customer a discount or credit to retain them",
      "D: Tell the customer you understand their frustration but these things sometimes take time",
    ],
    most:1, least:3,
    exp_most:"Escalating, taking ownership and committing to follow-up addresses the systemic failure rather than just managing symptoms.",
    exp_least:"Platitudes without action are the least effective response to a pattern of repeated failures."
  },
  {
    id:"cs09", d:2,
    context:"A customer asks you a detailed question about a product feature that you are not certain about. You have a rough idea of the answer but are not 100% sure.",
    opts:[
      "A: Give your best guess to appear knowledgeable and confident",
      "B: Tell the customer you are not sure and ask them to come back tomorrow",
      "C: Acknowledge that you want to give them accurate information, check the correct answer with a colleague or resource, and respond with the verified answer",
      "D: Tell the customer to look it up on the website",
    ],
    most:2, least:0,
    exp_most:"Checking before answering maintains trust and avoids misinforming the customer.",
    exp_least:"Guessing risks giving incorrect information that could damage the relationship or lead to a complaint."
  },
  {
    id:"cs10", d:2,
    context:"It is 4:58pm and your shift ends at 5:00pm. A customer joins the queue with what appears to be a complex issue. Your replacement colleague has not yet arrived.",
    opts:[
      "A: Serve the customer as quickly as possible and pass any unresolved items to your colleague via a note",
      "B: Tell the customer you are closed and they should come back tomorrow",
      "C: Rush through the customer interaction to finish by 5:00pm regardless of resolution quality",
      "D: Greet the customer, acknowledge the time, begin to assist them and brief your colleague fully when they arrive so there is a seamless handover",
    ],
    most:3, least:1,
    exp_most:"Seamless handover prioritises the customer while being fair to your colleague.",
    exp_least:"Turning away a customer who is already in queue is a failure of service responsibility."
  },
  {
    id:"cs11", d:3,
    context:"A customer is unhappy with a resolution you provided and says 'That is not acceptable. I want something better.' You believe your resolution was fair and consistent with company policy.",
    opts:[
      "A: Apologise and offer a better resolution to avoid conflict even if it breaks policy",
      "B: Remain calm, reaffirm the resolution with a brief explanation of why it is fair and within policy, express empathy for their dissatisfaction and offer to escalate to your supervisor if they wish",
      "C: Tell the customer the policy is the policy and the conversation is over",
      "D: Tell the customer you personally agree with them but your hands are tied",
    ],
    most:1, least:2,
    exp_most:"Maintaining a fair position with empathy and offering escalation is professional and consistent.",
    exp_least:"Ending the conversation abruptly is disrespectful and escalation-inducing."
  },
  {
    id:"cs12", d:1,
    context:"You notice that a colleague is providing incorrect information to customers about a new product promotion. Several customers have already been given wrong details.",
    opts:[
      "A: Wait until after the shift to mention it to the colleague",
      "B: Interrupt the colleague in front of the customer to correct them immediately",
      "C: Quietly and discreetly inform your colleague of the error as soon as a natural break occurs, and flag it to your supervisor so affected customers can be followed up",
      "D: Ignore it — it is not your responsibility",
    ],
    most:2, least:3,
    exp_most:"Discreet correction plus escalation to supervisor handles both the immediate error and the downstream customer impact.",
    exp_least:"Ignoring a known error that harms customers is a serious failure of service and teamwork."
  },
  {
    id:"cs13", d:2,
    context:"A customer calls in a language you do not speak fluently. You can understand roughly half of what they are saying.",
    opts:[
      "A: Ask the customer to call back when an agent who speaks their language is available",
      "B: Try to handle the call entirely on your own using the partial understanding you have",
      "C: Check if a bilingual colleague is immediately available to assist or interpret, and if not, offer the customer a scheduled callback with a suitable agent",
      "D: Speak very slowly and loudly in your language hoping the customer will understand",
    ],
    most:2, least:3,
    exp_most:"Finding language support or arranging a proper callback is respectful and ensures accurate service.",
    exp_least:"Speaking slowly and loudly in a different language is condescending and unhelpful."
  },
  {
    id:"cs14", d:3,
    context:"You receive a complaint from a customer about something that was clearly a mistake made by your organisation, not the customer. Your supervisor is not available right now.",
    opts:[
      "A: Tell the customer you cannot do anything without your supervisor's approval",
      "B: Take ownership, apologise clearly on behalf of the organisation, resolve whatever is within your authority to resolve, document the case fully and escalate to your supervisor as soon as they are available",
      "C: Acknowledge the mistake but explain all the reasons why it happened",
      "D: Offer the customer a small compensation immediately to close the complaint",
    ],
    most:1, least:2,
    exp_most:"Taking ownership, acting within authority and documenting for follow-up is the ideal approach to organisational error.",
    exp_least:"Explaining why the mistake happened rather than fixing it is defensive and customer-insensitive."
  },
  {
    id:"cs15", d:1,
    context:"During a very busy period, you are juggling four open chat conversations at the same time. One customer has been waiting for 6 minutes for a response.",
    opts:[
      "A: Continue handling the other three chats and get to the waiting customer when you can",
      "B: Send a quick acknowledgement to the waiting customer explaining you will respond in 2 minutes, then honour that commitment",
      "C: Close the waiting customer's chat to reduce your workload",
      "D: Ask a colleague to take over all four chats",
    ],
    most:1, least:2,
    exp_most:"A quick acknowledgement with a committed time manages expectations professionally.",
    exp_least:"Closing a customer's chat is the most damaging response — it creates a complaint."
  },
  {
    id:"cs16", d:2,
    context:"A customer is asking for a refund that you believe is fraudulent based on the purchase history pattern, but you have no definitive proof.",
    opts:[
      "A: Deny the refund and tell the customer you suspect fraud",
      "B: Process the refund as normal to avoid confrontation",
      "C: Follow the standard refund process while discreetly flagging the transaction to your fraud team or supervisor with the specific concerns, without accusing the customer",
      "D: Ask the customer aggressive questions to try to catch them out",
    ],
    most:2, least:0,
    exp_most:"Following process while flagging concerns to the right team is the correct balance between customer fairness and fraud prevention.",
    exp_least:"Accusing a customer of fraud without evidence is legally and reputationally dangerous."
  },
  {
    id:"cs17", d:3,
    context:"A customer leaves a very negative online review about your organisation. Your manager asks you to respond to the review on the company's behalf.",
    opts:[
      "A: Write a defensive response explaining why the customer was wrong",
      "B: Respond professionally, thank the customer for their feedback, acknowledge their experience, apologise for any shortfall and invite them to contact the team privately to resolve the matter",
      "C: Respond asking the customer to remove the review",
      "D: Do not respond — negative reviews are best left ignored",
    ],
    most:1, least:2,
    exp_most:"A professional, empathetic public response shows accountability and often converts a negative experience into a positive impression.",
    exp_least:"Asking a customer to remove a review is manipulative and likely to make the situation worse."
  },
  {
    id:"cs18", d:2,
    context:"You are serving a customer at a bank counter when you notice that the elderly customer seems confused about a large transfer they are requesting. They mention someone called them and told them to send the money.",
    opts:[
      "A: Process the transfer as instructed — it is the customer's money",
      "B: Politely pause the transaction, gently ask a few questions about the request to assess if this may be a scam, and involve your supervisor if you have concerns",
      "C: Refuse the transfer immediately and call the police",
      "D: Ask the customer to come back with a family member",
    ],
    most:1, least:0,
    exp_most:"Pausing and gently probing for potential fraud protects a vulnerable customer — this is a core banking safety responsibility.",
    exp_least:"Processing a potential scam transaction without any check exposes both the customer and the organisation to serious harm."
  },
  {
    id:"cs19", d:1,
    context:"A customer has been verbally abusive to you for the past two minutes, using personal insults. You have already asked them politely to refrain twice.",
    opts:[
      "A: Remain calm, clearly and professionally inform the customer that while you want to help them, you are unable to continue the conversation if the abusive language continues, and if it does, calmly end the interaction per your organisation's policy",
      "B: Match the customer's tone so they understand how it feels",
      "C: Start crying to make the customer feel guilty",
      "D: Continue to accept the abuse and serve the customer without comment",
    ],
    most:0, least:1,
    exp_most:"Assertively setting a boundary while offering to help, and acting on it professionally, is both effective and within best practice.",
    exp_least:"Matching aggression with aggression escalates the situation and is never appropriate."
  },
  {
    id:"cs20", d:3,
    context:"Your organisation has just changed a key policy that many customers are unhappy about. You are receiving many complaints about it. Personally, you also disagree with the change.",
    opts:[
      "A: Tell customers you personally agree with them that the policy is unfair",
      "B: Professionally explain the new policy, acknowledge that you understand why customers may be unhappy, focus on what you CAN do to help within the new guidelines, and pass aggregated feedback to your supervisor",
      "C: Refuse to engage with complaints about the policy",
      "D: Promise customers the policy will change if enough people complain",
    ],
    most:1, least:3,
    exp_most:"Explaining, empathising, focusing on available help and channelling feedback is the complete professional response.",
    exp_least:"Making promises about policy changes you have no authority to make raises false expectations and damages trust."
  },
];
